import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { ApiProblem } from '@/infrastructure/api/v1/problem';
import type { OrgInstance } from '@/types/orgTypes';

interface Options {
  userId: string | undefined;
  orgInstance: OrgInstance | null;
  orgLoading: boolean;
  orgError: Error | null;
  retryOrg: () => Promise<void>;
  onSignInNeeded: () => void;
}

export function useNewStory({ userId, orgInstance, orgLoading, orgError, retryOrg, onSignInNeeded }: Options) {
  const services = useServices();
  const navigate = useNavigate();
  const [isCreatingStory, setIsCreatingStory] = useState(false);
  // A project whose story failed is reused by the next try instead of left empty
  const emptyProjectRef = useRef<{ userId: string; projectId: string } | null>(null);

  const createProject = async (uid: string, orgInstanceId: string) => {
    const userProjects = await services.project.fetchProjectsForUser(uid);
    const untitledPattern = /^untitled project (\d+)$/i;
    let maxN = 0;
    for (const p of userProjects) {
      const match = p.projectName?.match(untitledPattern);
      if (match) {
        const n = parseInt(match[1], 10);
        if (n > maxN) maxN = n;
      }
    }

    const projectId = uuid();
    await services.project.addProjectInstanceRow({
      project_instance_id: projectId,
      user_id: uid,
      org_instance_id: orgInstanceId,
      project_instance_display_name: `Untitled project ${maxN + 1}`
    });
    services.analytics?.trackLureoNewProjectCreated({ project_id: projectId });
    return projectId;
  };

  const createStory = async () => {
    if (!userId) {
      onSignInNeeded();
      return;
    }
    if (!orgInstance?.orgInstanceId && (orgError || !orgLoading)) {
      toast.error('We could not load your account details.', { action: { label: 'Retry', onClick: () => void retryOrg() } });
      return;
    }
    if (!orgInstance?.orgInstanceId) {
      toast.error('Your organisation is still loading, please try again in a moment.');
      return;
    }
    if (isCreatingStory) return;

    setIsCreatingStory(true);

    try {
      const reusable = emptyProjectRef.current?.userId === userId ? emptyProjectRef.current.projectId : null;
      const projectId = reusable ?? (await createProject(userId, orgInstance.orgInstanceId));
      emptyProjectRef.current = { userId, projectId };

      const sceneResult = await services.scene.createNewScene(userId, navigate, projectId, undefined, undefined, { skipNavigate: true });
      if (!sceneResult?.sceneInstanceId) {
        throw new Error('Scene creation returned no scene id');
      }

      emptyProjectRef.current = null;
      navigate(`/editor/${sceneResult.sceneInstanceId}`);
    } catch (error) {
      if (error instanceof ApiProblem && error.status === 404) emptyProjectRef.current = null;
      console.error('Error creating new story:', error);
      toast.error('Could not create your story. Please try again.');
      setIsCreatingStory(false);
    }
  };

  const forgetProject = (projectId: string) => {
    if (emptyProjectRef.current?.projectId === projectId) emptyProjectRef.current = null;
  };

  return { createStory, isCreatingStory, forgetProject };
}
